import React, { useState } from 'react';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  Hand,
  Smile,
  Users,
  MessageSquare,
  FolderOpen,
  LayoutGrid,
  Maximize2,
  Settings,
  PhoneOff,
  ShieldAlert,
  MoreHorizontal,
  X,
} from 'lucide-react';
import { LayoutMode } from '../types';

interface ControlBarProps {
  isAudioMuted: boolean;
  isVideoOff: boolean;
  isScreenSharing: boolean;
  isHandRaised: boolean;
  layoutMode: LayoutMode;
  participantCount: number;
  unreadCount?: number;
  isHost: boolean;
  activePanel: 'participants' | 'chat' | 'files' | null;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onToggleHandRaise: () => void;
  onSendReaction: (emoji: string) => void;
  onToggleLayout: () => void;
  onTogglePanel: (panel: 'participants' | 'chat' | 'files') => void;
  onOpenSettings: () => void;
  onLeave: () => void;
  onEndMeeting?: () => void;
}

const REACTIONS = ['👍', '❤️', '👏', '🎉', '🔥', '😂'];

export const ControlBar: React.FC<ControlBarProps> = ({
  isAudioMuted,
  isVideoOff,
  isScreenSharing,
  isHandRaised,
  layoutMode,
  participantCount,
  unreadCount = 0,
  isHost,
  activePanel,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onToggleHandRaise,
  onSendReaction,
  onToggleLayout,
  onTogglePanel,
  onOpenSettings,
  onLeave,
  onEndMeeting,
}) => {
  const [showReactions, setShowReactions] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showMobileMore, setShowMobileMore] = useState(false);

  return (
    <div className="h-16 md:h-20 bg-[#0c0f17]/95 backdrop-blur-lg border-t border-slate-800/80 px-2 sm:px-4 md:px-6 flex items-center justify-between z-20 flex-shrink-0 select-none relative">
      {/* ================= DESKTOP LAYOUT (>= md) ================= */}
      {/* Left section: Layout mode & Settings */}
      <div className="hidden md:flex items-center space-x-2">
        <button
          onClick={onToggleLayout}
          title={layoutMode === 'grid' ? 'Switch to Speaker view' : 'Switch to Grid view'}
          className="p-2.5 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800/70 transition flex items-center space-x-1.5 text-xs font-medium"
        >
          {layoutMode === 'grid' ? <Maximize2 className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
          <span>{layoutMode === 'grid' ? 'Speaker View' : 'Grid View'}</span>
        </button>

        <button
          onClick={onOpenSettings}
          title="Device Settings"
          className="p-2.5 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800/70 transition"
        >
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {/* Center: Core Call Controls (Desktop) */}
      <div className="hidden md:flex items-center space-x-3">
        {/* Microphone */}
        <button
          onClick={onToggleAudio}
          title={isAudioMuted ? 'Unmute microphone' : 'Mute microphone'}
          className={`p-3.5 rounded-2xl transition shadow-sm ${
            isAudioMuted
              ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:bg-rose-500/30'
              : 'bg-slate-800/90 text-white hover:bg-slate-700 border border-slate-700/60'
          }`}
        >
          {isAudioMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>

        {/* Camera */}
        <button
          onClick={onToggleVideo}
          title={isVideoOff ? 'Start camera' : 'Stop camera'}
          className={`p-3.5 rounded-2xl transition shadow-sm ${
            isVideoOff
              ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:bg-rose-500/30'
              : 'bg-slate-800/90 text-white hover:bg-slate-700 border border-slate-700/60'
          }`}
        >
          {isVideoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
        </button>

        {/* Screen Share */}
        <button
          onClick={onToggleScreenShare}
          title={isScreenSharing ? 'Stop screen sharing' : 'Share your screen'}
          className={`p-3.5 rounded-2xl transition shadow-sm ${
            isScreenSharing
              ? 'bg-blue-600 text-white shadow-blue-500/20 shadow-lg'
              : 'bg-slate-800/90 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700/60'
          }`}
        >
          <Monitor className="w-5 h-5" />
        </button>

        {/* Raise Hand */}
        <button
          onClick={onToggleHandRaise}
          title={isHandRaised ? 'Lower hand' : 'Raise hand'}
          className={`p-3.5 rounded-2xl transition shadow-sm ${
            isHandRaised
              ? 'bg-amber-500 text-white shadow-amber-500/20 shadow-lg'
              : 'bg-slate-800/90 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700/60'
          }`}
        >
          <Hand className="w-5 h-5" />
        </button>

        {/* Reactions Popover */}
        <div className="relative">
          <button
            onClick={() => setShowReactions(!showReactions)}
            title="Send reaction"
            className="p-3.5 rounded-2xl bg-slate-800/90 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700/60 transition shadow-sm"
          >
            <Smile className="w-5 h-5" />
          </button>

          {showReactions && (
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-[#121622] border border-slate-700/80 p-2 rounded-2xl shadow-2xl flex items-center space-x-1.5 z-30">
              {REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onSendReaction(emoji);
                    setShowReactions(false);
                  }}
                  className="w-10 h-10 rounded-xl hover:bg-slate-800 flex items-center justify-center text-xl transition transform hover:scale-125"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right section: Side panels & Leave (Desktop) */}
      <div className="hidden md:flex items-center space-x-2">
        {/* Participants drawer toggle */}
        <button
          onClick={() => onTogglePanel('participants')}
          title="Participants list"
          className={`p-2.5 rounded-xl transition relative ${
            activePanel === 'participants'
              ? 'bg-blue-600 text-white'
              : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
          }`}
        >
          <Users className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-blue-500 text-[10px] font-bold text-white">
            {participantCount}
          </span>
        </button>

        {/* Chat drawer toggle */}
        <button
          onClick={() => onTogglePanel('chat')}
          title="Meeting chat"
          className={`p-2.5 rounded-xl transition relative ${
            activePanel === 'chat'
              ? 'bg-blue-600 text-white'
              : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
          }`}
        >
          <MessageSquare className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-rose-500 text-[10px] font-bold text-white">
              {unreadCount}
            </span>
          )}
        </button>

        {/* Shared Files drawer toggle */}
        <button
          onClick={() => onTogglePanel('files')}
          title="Shared files"
          className={`p-2.5 rounded-xl transition ${
            activePanel === 'files'
              ? 'bg-blue-600 text-white'
              : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
          }`}
        >
          <FolderOpen className="w-5 h-5" />
        </button>

        {/* Leave / End Button */}
        {isHost && onEndMeeting ? (
          <div className="relative">
            <button
              onClick={() => setShowEndConfirm(true)}
              className="px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold flex items-center space-x-1.5 shadow-lg shadow-rose-600/20 transition"
            >
              <PhoneOff className="w-4 h-4" />
              <span>End Call</span>
            </button>

            {showEndConfirm && (
              <div className="absolute bottom-16 right-0 w-64 bg-[#121622] border border-slate-700/80 rounded-2xl p-4 shadow-2xl z-30 space-y-3">
                <div className="flex items-center space-x-2 text-rose-400 font-semibold text-sm">
                  <ShieldAlert className="w-4 h-4" />
                  <span>Host Controls</span>
                </div>
                <p className="text-xs text-slate-400">Do you want to end the meeting for everyone or just leave?</p>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      setShowEndConfirm(false);
                      onEndMeeting();
                    }}
                    className="w-full py-2 bg-rose-600 hover:bg-rose-500 rounded-lg text-xs font-semibold text-white transition"
                  >
                    End Meeting for All
                  </button>
                  <button
                    onClick={() => {
                      setShowEndConfirm(false);
                      onLeave();
                    }}
                    className="w-full py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs font-semibold text-slate-200 transition"
                  >
                    Leave Meeting
                  </button>
                  <button
                    onClick={() => setShowEndConfirm(false)}
                    className="w-full py-1 text-slate-400 hover:text-slate-200 text-xs transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={onLeave}
            className="px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold flex items-center space-x-1.5 shadow-lg shadow-rose-600/20 transition"
          >
            <PhoneOff className="w-4 h-4" />
            <span>Leave</span>
          </button>
        )}
      </div>

      {/* ================= MOBILE COMPACT LAYOUT (< md) ================= */}
      <div className="flex md:hidden items-center justify-between w-full px-1">
        {/* Microphone */}
        <button
          onClick={onToggleAudio}
          title={isAudioMuted ? 'Unmute' : 'Mute'}
          className={`p-2.5 rounded-xl transition ${
            isAudioMuted
              ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
              : 'bg-slate-800/90 text-white'
          }`}
        >
          {isAudioMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>

        {/* Camera */}
        <button
          onClick={onToggleVideo}
          title={isVideoOff ? 'Start camera' : 'Stop camera'}
          className={`p-2.5 rounded-xl transition ${
            isVideoOff
              ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
              : 'bg-slate-800/90 text-white'
          }`}
        >
          {isVideoOff ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
        </button>

        {/* Chat Drawer Toggle */}
        <button
          onClick={() => onTogglePanel('chat')}
          title="Chat"
          className={`p-2.5 rounded-xl transition relative ${
            activePanel === 'chat'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-800/80 text-slate-300'
          }`}
        >
          <MessageSquare className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 px-1 rounded-full bg-rose-500 text-[9px] font-bold text-white">
              {unreadCount}
            </span>
          )}
        </button>

        {/* Participants Drawer Toggle */}
        <button
          onClick={() => onTogglePanel('participants')}
          title="Participants"
          className={`p-2.5 rounded-xl transition relative ${
            activePanel === 'participants'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-800/80 text-slate-300'
          }`}
        >
          <Users className="w-4 h-4" />
          <span className="absolute -top-1 -right-1 px-1 rounded-full bg-blue-500 text-[9px] font-bold text-white">
            {participantCount}
          </span>
        </button>

        {/* Raise Hand */}
        <button
          onClick={onToggleHandRaise}
          title="Raise hand"
          className={`p-2.5 rounded-xl transition ${
            isHandRaised
              ? 'bg-amber-500 text-white shadow-amber-500/20 shadow-md'
              : 'bg-slate-800/80 text-slate-300'
          }`}
        >
          <Hand className="w-4 h-4" />
        </button>

        {/* More Actions Toggle (Mobile Popover) */}
        <button
          onClick={() => setShowMobileMore(!showMobileMore)}
          title="More actions"
          className={`p-2.5 rounded-xl transition ${
            showMobileMore ? 'bg-blue-600 text-white' : 'bg-slate-800/80 text-slate-300'
          }`}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>

        {/* Leave Call Button */}
        <button
          onClick={isHost && onEndMeeting ? () => setShowEndConfirm(true) : onLeave}
          title="Leave meeting"
          className="p-2.5 rounded-xl bg-rose-600 text-white flex items-center justify-center shadow-md shadow-rose-600/30"
        >
          <PhoneOff className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile "More" Popover Menu */}
      {showMobileMore && (
        <div className="md:hidden absolute bottom-20 left-4 right-4 bg-[#121622]/98 border border-slate-700/90 rounded-2xl p-4 shadow-2xl z-40 space-y-3 backdrop-blur-xl animate-in slide-in-from-bottom-2 duration-150">
          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
            <span className="text-xs font-semibold text-slate-300">More Meeting Controls</span>
            <button
              onClick={() => setShowMobileMore(false)}
              className="p-1 text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            {/* Screen Share */}
            <button
              onClick={() => {
                setShowMobileMore(false);
                onToggleScreenShare();
              }}
              className={`p-2.5 rounded-xl border flex items-center space-x-2 transition ${
                isScreenSharing
                  ? 'bg-blue-600 text-white border-blue-400'
                  : 'bg-slate-800/80 text-slate-300 border-slate-700/60'
              }`}
            >
              <Monitor className="w-4 h-4" />
              <span>{isScreenSharing ? 'Stop Screen' : 'Share Screen'}</span>
            </button>

            {/* Layout Toggle */}
            <button
              onClick={() => {
                setShowMobileMore(false);
                onToggleLayout();
              }}
              className="p-2.5 rounded-xl bg-slate-800/80 text-slate-300 border border-slate-700/60 flex items-center space-x-2 transition"
            >
              {layoutMode === 'grid' ? <Maximize2 className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
              <span>{layoutMode === 'grid' ? 'Speaker View' : 'Grid View'}</span>
            </button>

            {/* Shared Files */}
            <button
              onClick={() => {
                setShowMobileMore(false);
                onTogglePanel('files');
              }}
              className="p-2.5 rounded-xl bg-slate-800/80 text-slate-300 border border-slate-700/60 flex items-center space-x-2 transition"
            >
              <FolderOpen className="w-4 h-4" />
              <span>Meeting Files</span>
            </button>

            {/* Device Settings */}
            <button
              onClick={() => {
                setShowMobileMore(false);
                onOpenSettings();
              }}
              className="p-2.5 rounded-xl bg-slate-800/80 text-slate-300 border border-slate-700/60 flex items-center space-x-2 transition"
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </button>
          </div>

          {/* Quick Reactions Bar in Mobile More Menu */}
          <div className="pt-2 border-t border-slate-800">
            <span className="text-[11px] text-slate-400 block mb-1.5 font-medium">Send Reaction</span>
            <div className="flex items-center justify-between">
              {REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onSendReaction(emoji);
                    setShowMobileMore(false);
                  }}
                  className="w-9 h-9 rounded-xl hover:bg-slate-800 flex items-center justify-center text-lg transition active:scale-125"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Host End Confirmation (Mobile fallback) */}
      {showEndConfirm && (
        <div className="md:hidden absolute bottom-20 left-4 right-4 bg-[#121622] border border-slate-700 rounded-2xl p-4 shadow-2xl z-40 space-y-3">
          <div className="flex items-center space-x-2 text-rose-400 font-semibold text-xs">
            <ShieldAlert className="w-4 h-4" />
            <span>Host Controls</span>
          </div>
          <p className="text-xs text-slate-400">Do you want to end the meeting for everyone or just leave?</p>
          <div className="space-y-2">
            <button
              onClick={() => {
                setShowEndConfirm(false);
                onEndMeeting?.();
              }}
              className="w-full py-2 bg-rose-600 text-white rounded-lg text-xs font-semibold"
            >
              End Meeting for All
            </button>
            <button
              onClick={() => {
                setShowEndConfirm(false);
                onLeave();
              }}
              className="w-full py-2 bg-slate-800 text-slate-200 rounded-lg text-xs font-semibold"
            >
              Leave Meeting
            </button>
            <button
              onClick={() => setShowEndConfirm(false)}
              className="w-full py-1 text-slate-400 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
